import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface Fornecedor {
  codigo?: string;
  cnpj: string;
  loja: string;
  nome: string;
}

interface ListResponse {
  success: boolean;
  data: Fornecedor[];
  page?: number;
  pageSize?: number;
  hasNext?: boolean;
}

export interface PageResult<T> {
  data: T[];
  hasNext: boolean;
}

interface SaveResponse {
  success: boolean;
  codigo: string;
  cnpj: string;
  loja: string;
  nome: string;
  message?: string;
}

interface SimpleResponse {
  success: boolean;
  message?: string;
}

@Injectable({ providedIn: 'root' })
export class FornecedoresOcultoService {
  private readonly base = 'portalnf/fornecedores-ocultos';

  constructor(private http: HttpClient) {}

  /**
   * Lista paginada. `busca` vai para o backend como LIKE (OR) em codigo, loja,
   * CNPJ e nome - filtrar no cliente so encontraria quem ja tivesse sido
   * paginado para a tela.
   */
  list(page = 1, pageSize = 10, busca = ''): Observable<PageResult<Fornecedor>> {
    let params = new HttpParams()
      .set('page', String(page))
      .set('pageSize', String(pageSize));
    const termo = (busca ?? '').trim();
    if (termo) { params = params.set('busca', termo); }
    return this.http.get<ListResponse>(this.base, { params }).pipe(
      map(r => ({ data: r.data ?? [], hasNext: !!r.hasNext }))
    );
  }

  create(f: Pick<Fornecedor, 'cnpj' | 'loja'> & Partial<Pick<Fornecedor, 'nome'>>): Observable<Fornecedor> {
    const body: Record<string, string> = { cnpj: f.cnpj, loja: f.loja };
    if (f.nome) body['nome'] = f.nome;
    return this.http.post<SaveResponse>(this.base, body)
      .pipe(map(r => ({ codigo: r.codigo, cnpj: r.cnpj, loja: r.loja, nome: r.nome })));
  }

  update(f: Required<Pick<Fornecedor, 'codigo' | 'loja' | 'nome'>>): Observable<Fornecedor> {
    return this.http.put<SaveResponse>(this.base, { codigo: f.codigo, loja: f.loja, nome: f.nome })
      .pipe(map(r => ({ codigo: r.codigo, cnpj: r.cnpj, loja: r.loja, nome: r.nome })));
  }

  delete(codigo: string, loja: string): Observable<void> {
    return this.http.request<SimpleResponse>('delete', this.base, { body: { codigo, loja } })
      .pipe(map(() => void 0));
  }
}
